import React, { useState, useEffect, useRef, useCallback } from "react";
import { Tab, Tabs, Card } from "react-bootstrap";
import * as THREE from "three";
import "bootstrap/dist/css/bootstrap.min.css";
import "./neonstyle.css";

const MugCanvas = () => {
  const [frontText, setFrontText] = useState("Ön Taraf");
  const [backText, setBackText] = useState("Arka Taraf");
  const [frontFontFamily, setFrontFontFamily] = useState("Dancing Script");
  const [backFontFamily, setBackFontFamily] = useState("Dancing Script");
  const [frontColor, setFrontColor] = useState("rgb(0, 0, 0)");
  const [backColor, setBackColor] = useState("rgb(0, 0, 0)");
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const mugRef = useRef(null);
  const hiddenCanvasRef = useRef(null);

  // Initialize Three.js for 3D preview
  useEffect(() => {
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(75, 600 / 400, 0.1, 1000);
    camera.position.set(0, 0, 4);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(600, 400);
    renderer.setClearColor(0x000000, 0);
    canvasRef.current.innerHTML = "";
    canvasRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create a more realistic mug shape
    const mugGroup = new THREE.Group();

    // Mug body (tapered cylinder)
    const mugGeometry = new THREE.CylinderGeometry(1.5, 1.3, 3, 32, 1, false);
    const mugMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff });
    const mugBody = new THREE.Mesh(mugGeometry, mugMaterial);
    mugGroup.add(mugBody);

    // Mug handle
    const handleGeometry = new THREE.TorusGeometry(0.8, 0.15, 16, 32, Math.PI * 2);
    const handleMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    handle.position.set(1.5, 0, 0);
    handle.rotation.set(0, 0, Math.PI / 2);
    mugGroup.add(handle);

    mugGroup.position.set(0, 0, 0);
    scene.add(mugGroup);
    mugRef.current = mugBody;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // Responsive canvas resizing
    const resizeCanvas = () => {
      const container = canvasRef.current;
      const width = container.clientWidth;
      const height = width * (2 / 3); // Maintain aspect ratio
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const animate = () => {
      requestAnimationFrame(animate);
      mugGroup.rotation.y += 0.01;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      renderer.dispose();
    };
  }, []);

  // Update mug texture
  const updateMugTexture = useCallback(() => {
    if (!mugRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Front design
    if (frontImage) {
      const img = new Image();
      img.src = frontImage;
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const maxWidth = canvas.width * 0.25;
        const maxHeight = canvas.height * 0.5;
        let drawWidth = maxWidth;
        let drawHeight = drawWidth / aspectRatio;

        if (drawHeight > maxHeight) {
          drawHeight = maxHeight;
          drawWidth = drawHeight * aspectRatio;
        }

        const offsetX = (canvas.width * 0.25 - drawWidth) / 2;
        const offsetY = (canvas.height - drawHeight) / 2;

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        applyTexture(canvas);
      };
    } else {
      ctx.font = `40px ${frontFontFamily}`;
      ctx.fillStyle = frontColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(frontText, canvas.width * 0.125, canvas.height / 2);
    }

    // Back design
    if (backImage) {
      const img = new Image();
      img.src = backImage;
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const maxWidth = canvas.width * 0.25;
        const maxHeight = canvas.height * 0.5;
        let drawWidth = maxWidth;
        let drawHeight = drawWidth / aspectRatio;

        if (drawHeight > maxHeight) {
          drawHeight = maxHeight;
          drawWidth = drawHeight * aspectRatio;
        }

        const offsetX = canvas.width * 0.5 + (canvas.width * 0.25 - drawWidth) / 2;
        const offsetY = (canvas.height - drawHeight) / 2;

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        applyTexture(canvas);
      };
    } else {
      ctx.font = `40px ${backFontFamily}`;
      ctx.fillStyle = backColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(backText, canvas.width * 0.625, canvas.height / 2);
    }

    applyTexture(canvas);
  }, [frontText, backText, frontFontFamily, backFontFamily, frontColor, backColor, frontImage, backImage]);

  const applyTexture = (canvas) => {
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 1);
    mugRef.current.material.map = texture;
    mugRef.current.material.needsUpdate = true;
  };

  useEffect(() => {
    updateMugTexture();
  }, [updateMugTexture]);

  const handleFrontTextChange = (e) => setFrontText(e.target.value);
  const handleBackTextChange = (e) => setBackText(e.target.value);
  const handleFrontFontChange = (font) => setFrontFontFamily(font);
  const handleBackFontChange = (font) => setBackFontFamily(font);
  const handleFrontImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setFrontImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };
  const handleBackImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setBackImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleWhatsAppClick = async () => {
    try {
      const whatsappMessage = `Ön Taraf - Metin: ${frontText}\nFont: ${frontFontFamily}\nRenk: ${frontColor}\nArka Taraf - Metin: ${backText}\nFont: ${backFontFamily}\nRenk: ${backColor}\nTasarım Görseli:\n(Lütfen bu mesajı gönderdikten sonra tasarım görselini WhatsApp üzerinden manuel olarak paylaşın)`;
      const phoneNumber = "+905524543679";
      const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error generating WhatsApp message:", error);
      const message = `Ön Taraf - Metin: ${frontText}\nFont: ${frontFontFamily}\nRenk: ${frontColor}\nArka Taraf - Metin: ${backText}\nFont: ${backFontFamily}\nRenk: ${backColor}\n(Görsel oluşturulamadı, lütfen tasarımı tarif edin)`;
      const phoneNumber = "+905524543679";
      const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }
  };

  const fonts = [
    "Dancing Script", "Caveat", "Shadows Into Light", "Indie Flower", "Amatic SC",
    "Permanent Marker", "Architects Daughter", "Gloria Hallelujah", "Satisfy", "Patrick Hand"
  ];

  const neonColors = [
    { name: "Siyah", value: "rgb(0, 0, 0)" },
    { name: "Kırmızı", value: "rgb(205, 46, 31)" },
    { name: "Mavi", value: "rgb(52, 152, 219)" },
    { name: "Yeşil", value: "rgb(46, 204, 113)" },
  ];

  return (
    <div className="main-container">
      <div className="headarc">
        <h1 className="title">Yadi Baskı ile Kendi Bardağını Tasarla</h1>
      </div>

      <div className="content-wrapper">
        <Card className="control-panel shadow-lg rounded-4">
          <Tabs defaultActiveKey="front" className="custom-tabs">
            <Tab eventKey="front" title="ÖN TARAF">
              <Tabs defaultActiveKey="front-text" className="custom-tabs">
                <Tab eventKey="front-text" title="YAZI" className="tab-content">
                  <textarea
                    className="form-control text-input"
                    rows={4}
                    value={frontText}
                    onChange={handleFrontTextChange}
                    placeholder="Bardağın ön tarafına yazılacak metni girin!"
                  />
                </Tab>
                <Tab eventKey="front-font" title="FONT" className="tab-content">
                  <h5 className="section-title">FONT SEÇ</h5>
                  <div className="font-grid">
                    {fonts.map((font, index) => (
                      <div
                        key={index}
                        onClick={() => handleFrontFontChange(font)}
                        className={`font-option ${frontFontFamily === font ? 'active' : ''}`}
                        style={{ fontFamily: font }}
                      >
                        {font}
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="front-color" title="RENK" className="tab-content">
                  <h5 className="section-title">RENK SEÇ</h5>
                  <div className="color-grid">
                    {neonColors.map((colorOption, index) => (
                      <div key={index} className="color-option">
                        <div
                          className={`color-circle ${frontColor === colorOption.value ? 'active' : ''}`}
                          style={{ backgroundColor: colorOption.value }}
                          onClick={() => setFrontColor(colorOption.value)}
                        />
                        <span className="color-name">{colorOption.name}</span>
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="front-image" title="GÖRSEL" className="tab-content">
                  <h5 className="section-title">GÖRSEL YÜKLE</h5>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFrontImageUpload}
                    className="form-control"
                  />
                </Tab>
              </Tabs>
            </Tab>

            <Tab eventKey="back" title="ARKA TARAF">
              <Tabs defaultActiveKey="back-text" className="custom-tabs">
                <Tab eventKey="back-text" title="YAZI" className="tab-content">
                  <textarea
                    className="form-control text-input"
                    rows={4}
                    value={backText}
                    onChange={handleBackTextChange}
                    placeholder="Bardağın arka tarafına yazılacak metni girin!"
                  />
                </Tab>
                <Tab eventKey="back-font" title="FONT" className="tab-content">
                  <h5 className="section-title">FONT SEÇ</h5>
                  <div className="font-grid">
                    {fonts.map((font, index) => (
                      <div
                        key={index}
                        onClick={() => handleBackFontChange(font)}
                        className={`font-option ${backFontFamily === font ? 'active' : ''}`}
                        style={{ fontFamily: font }}
                      >
                        {font}
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="back-color" title="RENK" className="tab-content">
                  <h5 className="section-title">RENK SEÇ</h5>
                  <div className="color-grid">
                    {neonColors.map((colorOption, index) => (
                      <div key={index} className="color-option">
                        <div
                          className={`color-circle ${backColor === colorOption.value ? 'active' : ''}`}
                          style={{ backgroundColor: colorOption.value }}
                          onClick={() => setBackColor(colorOption.value)}
                        />
                        <span className="color-name">{colorOption.name}</span>
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="back-image" title="GÖRSEL" className="tab-content">
                  <h5 className="section-title">GÖRSEL YÜKLE</h5>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBackImageUpload}
                    className="form-control"
                  />
                </Tab>
              </Tabs>
            </Tab>
          </Tabs>

          <p className="info-text">
            Tasarımına karar verdiğiniz ürünün fiyatını öğrenmek için WhatsApp üzerinden bizimle iletişime geçebilirsiniz.
          </p>

          <div className="button-group">
            <button className="whatsapp-btn" onClick={handleWhatsAppClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-whatsapp"
                viewBox="0 0 16 16"
              >
                <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
              </svg>
              WHATSAPP İLE İLETİŞİME GEÇ
            </button>
          </div>
        </Card>

        <div ref={canvasRef} className="canvas-container" />
      </div>

      <canvas ref={hiddenCanvasRef} className="hidden-canvas" />
      <div className="powered-by-section">
        <p>
          Powered by <a href="https://hacialikara.com" target="_blank" rel="noopener noreferrer">H.Ali.K.</a>
        </p>
      </div>
    </div>
  );
};

export default MugCanvas;